import { onScopeDispose, ref, watch } from 'vue'

import type { Ref } from 'vue'
import type { PipLabels } from '@utils/pip'

import { PipWindow } from '@utils/pip'

interface UsePipOptions {
  tutorTrack: Ref<MediaStreamTrack | null>
  studentTrack: Ref<MediaStreamTrack | null>
  micEnabled: Ref<boolean>
  camEnabled: Ref<boolean>
  // Absent participant -> tile hidden entirely; a null track alone means "camera off".
  tutorPresent?: Ref<boolean>
  studentPresent?: Ref<boolean>
  onToggleMic: () => void
  onToggleCam: () => void
  labels?: Partial<PipLabels>
}

interface UsePipReturn {
  isSupported: boolean
  isActive: Ref<boolean>
  toggle: () => Promise<void>
}

export const usePip = (options: UsePipOptions): UsePipReturn => {
  const isSupported = PipWindow.isSupported()
  const isActive = ref<boolean>(false)

  const tutorPresent = options.tutorPresent ?? ref(true)
  const studentPresent = options.studentPresent ?? ref(true)

  let isOpening = false

  const syncTracks = (): void => {
    PipWindow.updateTracks({
      tutor: options.tutorTrack.value,
      student: options.studentTrack.value,
    })
  }

  const syncState = (): void => {
    PipWindow.updateState({
      micEnabled: options.micEnabled.value,
      camEnabled: options.camEnabled.value,
    })
  }

  const syncPresence = (): void => {
    PipWindow.updatePresence({
      tutor: tutorPresent.value,
      student: studentPresent.value,
    })
  }

  const open = async (): Promise<void> => {
    const opened = await PipWindow.open({
      tracks: {
        tutor: options.tutorTrack.value,
        student: options.studentTrack.value,
      },
      state: {
        micEnabled: options.micEnabled.value,
        camEnabled: options.camEnabled.value,
      },
      presence: {
        tutor: tutorPresent.value,
        student: studentPresent.value,
      },
      handlers: {
        onToggleMic: options.onToggleMic,
        onToggleCam: options.onToggleCam,
        onClose: () => {
          isActive.value = false
        },
      },
      labels: options.labels,
    })

    isActive.value = opened

    // tracks/state may have changed while requestWindow was pending
    if (opened) {
      syncTracks()
      syncState()
      syncPresence()
    }
  }

  const toggle = async (): Promise<void> => {
    if (isOpening) return

    if (PipWindow.isOpen()) {
      PipWindow.close()

      return
    }

    isOpening = true
    try {
      await open()
    } finally {
      isOpening = false
    }
  }

  watch([options.tutorTrack, options.studentTrack], syncTracks)
  watch([options.micEnabled, options.camEnabled], syncState)
  watch([tutorPresent, studentPresent], syncPresence)

  onScopeDispose(() => {
    PipWindow.close()
  })

  return {
    isSupported,
    isActive,
    toggle,
  }
}
